import { useLocation } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { PopupHook, WaveListHook } from '../../../../../hooks';
import {
  AssignWaveToUserPopup,
  WaveAntTable,
  WaveBulkToolbar,
} from '../../../../../page-components';
import {
  CommonUtility,
  Tabs,
  ToastMessage,
  WaveService,
  WaveTypes,
} from '../../../../../utility';
import { useWavesFilterContext } from '.';
import { MergeWavePopup } from './merge-popup';
import { ConfirmationPopup } from '@components/index';

export function WaveListTab({ refreshKey, setStatuses, refresh }) {
  const { pathname } = useLocation();
  const [checkedWaves, setCheckedWaves] = useState({});
  const status = useMemo(() => {
    if (location.pathname) {
      return CommonUtility.getSegmentByPosition(location.pathname, 5);
    }
    return null;
  }, [pathname]);

  const [mergeWavePopup, setMergeWavePopup] = useState(false);
  const [assignWaveUserPopup, setAssignWaveUserPopup] = useState(false);
  const {
    open: unAssignPopup,
    handleClose: closeUnAssignPopup,
    handleOpen: openUnAssignPopup,
    loading: unAssignLoading,
    setLoading: setUnAssignLoading,
  } = PopupHook();

  const { warehouseId, search, setWaveBulkActions } = useWavesFilterContext();

  const statuses = useMemo(() => {
    if (status === 'all') {
      return encodeURI([
        Tabs.WavesTabs.building,
        Tabs.WavesTabs.ready,
        Tabs.WavesTabs.in_progress,
        Tabs.WavesTabs.sorting,
      ]);
    }
    return status;
  }, [status]);

  const { data, totalData, loading, filter, filterChanged, pageChanged } = WaveListHook(
    statuses,
    warehouseId,
    search,
    refreshKey,
  );

  useEffect(() => {
    if (Array.isArray(statuses)) {
      setStatuses(statuses);
    } else if (statuses) {
      setStatuses([statuses]);
    }
  }, [statuses, setStatuses]);

  const checkWave = wave => {
    setCheckedWaves(waves => ({
      ...waves,
      [wave._id]: wave,
    }));
  };

  const unCheckWave = wave => {
    setCheckedWaves(waves => ({
      ...waves,
      [wave._id]: null,
    }));
  };

  const resetCheck = () => {
    setCheckedWaves({});
  };

  useEffect(() => {
    resetCheck();
  }, [statuses]);

  const filteredData = useMemo(() => {
    const arr = [...data];
    Object.keys(checkedWaves).forEach(waveId => {
      // If wave is checked
      if (checkedWaves[waveId]) {
        const checkedWaveInData = arr.find(x => x._id === waveId);
        if (!checkedWaveInData) {
          arr.unshift(checkedWaves[waveId]);
        }
      }
    });

    const seen = new Set();
    return arr.filter(wave => {
      const id = wave?._id;
      if (!id) return true;
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  }, [data, checkedWaves]);

  const canWavesBeMerged = useMemo(() => {
    if (!CommonUtility.isValidObject(checkedWaves)) return false;

    const waveTypes = Object.values(checkedWaves)
      .filter(CommonUtility.isValidObject)
      .map(x => x.metadata.waveType);

    if (waveTypes.length === 0) return false;

    const allShipInBox = waveTypes.every(type => type === WaveTypes.shipInBox);
    const allNormalWaves = waveTypes.every(
      type => type === WaveTypes.multiItemOrders || type === WaveTypes.singleItemOrder,
    );

    return allShipInBox || allNormalWaves;
  }, [checkedWaves]);

  const {
    count: selectedWaves,
    waveIds,
    wavesWithUsers,
  } = useMemo(() => {
    const waveIds = [];
    const wavesWithUsers = [];
    Object.values(checkedWaves).forEach(x => {
      if (
        CommonUtility.isValidObject(x) &&
        CommonUtility.isValidObject(x.assignedTo?.userId)
      ) {
        wavesWithUsers.push(x);
      }
      if (CommonUtility.isValidObject(x)) {
        waveIds.push(x._id);
      }
    });
    return { count: waveIds?.length, waveIds, wavesWithUsers };
  }, [checkedWaves]);

  const closeAssignUserPopup = () => {
    setAssignWaveUserPopup(false);
  };

  const openAssignUserPopup = () => {
    setAssignWaveUserPopup(true);
  };

  const unAssignWaves = async () => {
    try {
      setUnAssignLoading(true);
      await WaveService.unAssignUserFromWaves({
        waveIds: wavesWithUsers.map(x => x._id),
      });
      refresh();
      resetCheck();
      closeUnAssignPopup();
      ToastMessage.success('Picker unassigned successfully');
    } catch (error) {
      ToastMessage.apiError(error);
    } finally {
      setUnAssignLoading(false);
    }
  };

  const openMergePopup = useCallback(() => setMergeWavePopup(true), []);

  useEffect(() => {
    if (!setWaveBulkActions) return undefined;

    setWaveBulkActions(
      <WaveBulkToolbar
        selectedCount={selectedWaves}
        canWavesBeMerged={canWavesBeMerged}
        wavesWithUsersCount={wavesWithUsers?.length ?? 0}
        onMerge={openMergePopup}
        onAssignPicker={openAssignUserPopup}
        onUnassignPicker={openUnAssignPopup}
      />,
    );

    return () => setWaveBulkActions(null);
  }, [
    setWaveBulkActions,
    selectedWaves,
    canWavesBeMerged,
    wavesWithUsers,
    openMergePopup,
    openAssignUserPopup,
    openUnAssignPopup,
  ]);

  return (
    <div className="mt-4">
      <WaveAntTable
        loading={loading}
        data={filteredData || []}
        totalData={totalData}
        filter={filter}
        filterChanged={filterChanged}
        pageChanged={pageChanged}
        checkedWaves={checkedWaves}
        checkWave={checkWave}
        unCheckWave={unCheckWave}
        allowMerging={[Tabs.WavesTabs.building, Tabs.WavesTabs.ready].includes(status)}
      />
      <MergeWavePopup
        checkedWaves={checkedWaves}
        open={mergeWavePopup}
        onClose={() => setMergeWavePopup(false)}
        warehouseId={warehouseId}
        refresh={refresh}
        resetCheck={resetCheck}
      />
      <AssignWaveToUserPopup
        waveIds={waveIds}
        open={assignWaveUserPopup}
        onClose={closeAssignUserPopup}
        refresh={refresh}
        resetCheck={resetCheck}
      />
      <ConfirmationPopup
        open={unAssignPopup}
        onClose={closeUnAssignPopup}
        onConfirm={unAssignWaves}
        loading={unAssignLoading}
        title="Unassign Picker"
        message={`Are you sure to unassign picker(s) from ${wavesWithUsers
          ?.map(x => x.waveNumber)
          .join(', ')}`}
      />
    </div>
  );
}
